import { useCallback, useState, type ReactNode } from "react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar"
import { SidebarGlyphSlot } from "@/components/sidebar/sidebar-glyph-slot"
import {
  OrgSwitcherAvatar,
  OrganisationSwitcherRow,
  sortOrganisationsForMenu,
} from "@/components/organisation/organisation-switcher-ui"
import { useEmployerOrganisationOptional } from "@/context/employer-organisation-context"
import {
  EMPLOYER_HOME_PATH,
  employerOrganisationHubHref,
  managedEmployerOrganisationSwitcherEnabled,
} from "@/constants"
import { useArtifactCanvas } from "@/components/artifact/artifact-canvas-context"
import { cn } from "@/lib/utils"
import { ChevronDown, Plus, Settings } from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { navigateWithThreadPreservation, getHrefWithThreadPreservation } from "@/utils/thread-navigation"
import { buildSettingsEnterState } from "@/constants/settings-nav"
import {
  EMPLOYER_ORGANISATION_SETTINGS_GENERAL_SEGMENT,
  employerOrganisationSettingsPath,
} from "@/constants/employer-organisation-settings-nav"

/** Matches profile empty-state add CTAs (`profile-section-skills-card`). */
const createOrganisationCtaClass =
  "h-auto w-full justify-start border-dashed px-4 py-4 text-muted-foreground"

/** Fragment of `SidebarMenuItem`s for employer workspace switching (opt-in via pack config). */
export function EmployerOrganisationSwitcherItems(): ReactNode {
  const ctx = useEmployerOrganisationOptional()
  const navigate = useNavigate()
  const location = useLocation()
  const { closeCanvas } = useArtifactCanvas()
  const { effectiveCollapsed, isMobile: sidebarIsMobile } = useSidebar()
  const [menuOpen, setMenuOpen] = useState(false)

  const openCreateOrganisationCanvas = useCallback(() => {
    if (!ctx?.enabled) return
    setMenuOpen(false)
    navigateWithThreadPreservation(
      navigate,
      employerOrganisationHubHref({ openCreate: true }),
      new URLSearchParams(window.location.search),
    )
  }, [ctx?.enabled, navigate])

  const openOrganisationSettings = useCallback(
    (slug: string) => {
      setMenuOpen(false)
      navigate(
        employerOrganisationSettingsPath(slug, EMPLOYER_ORGANISATION_SETTINGS_GENERAL_SEGMENT),
        {
          state: buildSettingsEnterState({
            from: `${location.pathname}${location.search}`,
          }),
        },
      )
    },
    [location.pathname, location.search, navigate],
  )

  if (!managedEmployerOrganisationSwitcherEnabled() || !ctx) return null

  const {
    organisations,
    organisationsLoading,
    activeOrganisation,
    activeOrganisationSlug,
    setActiveOrganisationSlug,
  } = ctx

  const collapsed = effectiveCollapsed
  const sortedOrganisations = sortOrganisationsForMenu(organisations, activeOrganisationSlug)
  const triggerName = organisationsLoading ? "Loading…" : (activeOrganisation?.name ?? "Organisation")
  const hasNoOrganisations = !organisationsLoading && organisations.length === 0

  const handleSwitchOrganisation = (slug: string) => {
    if (slug === activeOrganisationSlug) {
      setMenuOpen(false)
      return
    }
    setMenuOpen(false)
    closeCanvas()
    const onInterviewer = location.pathname.startsWith("/interviewer")
    if (onInterviewer) {
      setActiveOrganisationSlug(slug)
      return
    }
    const homeTarget = getHrefWithThreadPreservation(EMPLOYER_HOME_PATH)
    // Leave deep routes before invalidating org-scoped queries (job workspace redirects on fetch error).
    navigate(homeTarget, { replace: true })
    setActiveOrganisationSlug(slug, { deferInvalidate: true })
  }

  if (organisationsLoading) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          disabled
          tooltip={collapsed ? "Loading organisations" : undefined}
        >
          <SidebarGlyphSlot>
            <OrgSwitcherAvatar name="Loading…" />
          </SidebarGlyphSlot>
          {!collapsed ? (
            <span className="truncate flex-1 min-w-0 text-left font-semibold text-sm">Loading…</span>
          ) : null}
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  if (hasNoOrganisations) {
    return (
      <SidebarMenuItem>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className={cn(
                createOrganisationCtaClass,
                collapsed && "size-8 w-8 justify-center p-0",
                !collapsed && "gap-2 px-0",
              )}
              onClick={openCreateOrganisationCanvas}
            >
              <SidebarGlyphSlot>
                <Plus className="size-4 shrink-0" aria-hidden />
              </SidebarGlyphSlot>
              {collapsed ? (
                <span className="sr-only">Create organisation</span>
              ) : (
                "Create organisation"
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" align="center" hidden={!collapsed || sidebarIsMobile}>
            Create organisation
          </TooltipContent>
        </Tooltip>
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            className="data-[state=open]:bg-muted"
            tooltip={
              collapsed
                ? activeOrganisation
                  ? `${activeOrganisation.name} — switch workspace`
                  : "Switch workspace"
                : undefined
            }
          >
            <SidebarGlyphSlot>
              <OrgSwitcherAvatar
                name={activeOrganisation?.name ?? triggerName}
                logoUrl={activeOrganisation?.logo_url}
              />
            </SidebarGlyphSlot>
            {!collapsed ? (
              <span className="truncate flex-1 min-w-0 text-left font-semibold text-sm">
                {triggerName}
              </span>
            ) : null}
            {!collapsed ? (
              <ChevronDown
                className={cn(
                  "size-4 shrink-0 opacity-50 transition-transform",
                  menuOpen && "rotate-180",
                )}
              />
            ) : null}
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          side="bottom"
          align="start"
          sideOffset={4}
          collisionPadding={8}
          className="flex w-[var(--radix-dropdown-menu-trigger-width)] flex-col overflow-hidden p-0"
        >
          <div className="max-h-[352px] overflow-y-auto p-1">
            <div className="px-2 py-1.5 text-[11px] tracking-wide text-muted-foreground">
              Organisations
            </div>
            {sortedOrganisations.map((org) => (
              <OrganisationSwitcherRow
                key={org.id}
                org={org}
                isActive={activeOrganisationSlug === org.slug}
                onSwitch={() => handleSwitchOrganisation(org.slug)}
              />
            ))}
          </div>
          <DropdownMenuSeparator className="shrink-0" />
          <div className="shrink-0 p-1">
            {activeOrganisationSlug ? (
              <DropdownMenuItem
                className="min-h-11 gap-2"
                onSelect={() => {
                  openOrganisationSettings(activeOrganisationSlug)
                }}
              >
                <Settings className="size-4 shrink-0 text-muted-foreground" aria-hidden />
                <span>Organisation settings</span>
              </DropdownMenuItem>
            ) : null}
            <DropdownMenuItem
              className="min-h-11 gap-2"
              onSelect={() => {
                openCreateOrganisationCanvas()
              }}
            >
              <Plus className="size-4 shrink-0 text-muted-foreground" aria-hidden />
              <span>Create organisation</span>
            </DropdownMenuItem>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  )
}
